import dayjs, { extend } from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { findTimeZone, getZonedTime } from 'timezone-support';

extend(customParseFormat);
extend(timezone);
extend(utc);
extend(advanced);
extend(relativeTime);
extend(minMax);

// Custom timezone abbreviation function
const getTimezoneAbbreviation = (date: Date, timezone: string): string => {
  const tz = findTimeZone(timezone);
  const now = new Date();
  const zoned = getZonedTime(now, tz);
  if (zoned?.zone?.abbreviation) {
    return zoned.zone.abbreviation;
  }
  // For non-European timezones, use the default behavior
  const formatter = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  return parts.find(part => part.type === 'timeZoneName')?.value || '';
};

// Override the format function to handle custom timezone abbreviations
const originalFormat = dayjs.prototype.format;
dayjs.prototype.format = function (formatStr: string) {
  if (formatStr && formatStr.includes('z') && this.$x?.$timezone) {
    const timezone = this.$x.$timezone;
    const abbreviation = getTimezoneAbbreviation(this.toDate(), timezone);

    // Replace 'z' with the custom abbreviation, wrapped in brackets to treat as literal
    const customFormatStr = formatStr.replace(/z/g, `[${abbreviation}]`);
    return originalFormat.call(this, customFormatStr);
  }
  return originalFormat.call(this, formatStr);
};

export const TZ = 'America/New_York';
dayjs.tz.setDefault(TZ);
